/**
 * WhySameWeightCallout — explains "why is this active item getting equal
 * weight with everyone else?" in plain language.
 *
 * The optimizer clamps a layer's raw scores at `Math.max(0, raw)` and, if
 * every active sibling ends up at 0, falls back to an equal split. The
 * three common root causes are:
 *
 *  1. The formula is the literal string `0` (the user never picked a metric
 *     in Sliders mode, or wrote `0` in the master expression).
 *  2. The formula evaluated to a negative number for every item (e.g.
 *     `profit` when revenue is 0).
 *  3. The cube returned no metric values for this item in the chosen window.
 *
 * Surface the right one so users can fix the formula in one click.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import type { PreviewBreakdownEntry, PreviewRow } from '../../preview-row.types';

export interface WhySameWeightCalloutProps {
  row: PreviewRow;
}

const WhySameWeightCallout: FC<WhySameWeightCalloutProps> = ({ row }) => {
  const diagnosis = diagnoseEqualSplit(row);
  if (!diagnosis) return null;
  return (
    <div className="rounded border border-amber-400/40 bg-amber-500/5 px-2 py-1.5 text-[10px] leading-snug text-amber-700 dark:text-amber-300">
      <div className="mb-0.5 font-semibold uppercase tracking-wide">
        Why same weight as siblings?
      </div>
      <p>{diagnosis}</p>
    </div>
  );
};

function diagnoseEqualSplit(row: PreviewRow): string | null {
  if (!row.active) return null;
  if (row.excluded) return null;
  if (row.passThrough) return null;
  if ((row.rawScore ?? 0) > 0) return null;
  const breakdown = row.breakdown ?? [];
  if (breakdown.length === 0) return null;

  if (everyExpressionIsZero(breakdown)) {
    return 'Every formula group is the literal value `0` — no item can score above 0. Pick at least one metric in Sliders mode, or write a real expression (e.g. `profit`, `roi`, `revenue * cr`).';
  }
  if (anyNegativeRawScore(breakdown)) {
    return 'The formula evaluated to a negative number for this item, which the optimizer clamps to 0 (so a negative score never beats a zero score). Use a non-negative metric such as `revenue`, `epc`, or `conversions`, or shift the expression (e.g. `roi + 100`).';
  }
  if (everyMetricsBagIsEmpty(breakdown)) {
    return 'The tracker cube returned no metric values for this item in the chosen date range. Verify the date window and that the item recorded traffic.';
  }
  return 'The formula evaluated to 0 for this item with the metrics available. Try a different expression that varies across siblings.';
}

function everyExpressionIsZero(entries: PreviewBreakdownEntry[]): boolean {
  return entries.every((e) => (e.expression ?? '').trim() === '0');
}

function anyNegativeRawScore(entries: PreviewBreakdownEntry[]): boolean {
  return entries.some((e) => (e.rawScore ?? 0) < 0);
}

function everyMetricsBagIsEmpty(entries: PreviewBreakdownEntry[]): boolean {
  return entries.every((e) => !e.metrics || Object.keys(e.metrics).length === 0);
}

export default WhySameWeightCallout;
